import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./authorization.functions";

export const getAIChatSettings = createServerFn({ method: "GET" })
  .handler(async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("ai_chat_settings")
      .select("*")
      .eq("id", 1)
      .single();

    if (error) throw new Error("Failed to fetch AI settings");
    return data;
  });

export const updateAIChatSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({
    enabled: z.boolean().optional(),
    provider: z.string().optional(),
    model_id: z.string().optional(),
    temperature: z.number().optional(),
    max_tokens: z.number().optional(),
    system_instructions: z.string().optional(),
    welcome_message: z.string().optional(),
    fallback_message: z.string().optional(),
    api_key: z.string().optional(),
  }).parse(data))
  .handler(async ({ context, data }) => {
    if (!context?.supabase) throw new Error("Supabase client not available");
    await assertAdmin(context.supabase);
    
    const { api_key, ...settings } = data;
    const updatePayload: any = { ...settings };
    if (api_key !== undefined) {
      updatePayload.api_key = api_key;
    }
    
    const { error } = await context.supabase
      .from("ai_chat_settings")
      .update(updatePayload)
      .eq("id", 1);

    if (error) throw new Error("Failed to update AI settings");

    return { success: true };
  });

export const testAIChatConnection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ message: z.string() }).parse(data))
  .handler(async ({ context, data }) => {
    if (!context?.supabase) throw new Error("Supabase client not available");
    await assertAdmin(context.supabase);
    const { callOpenRouter } = await import("./ai-chat.server");
    
    const { data: settings } = await context.supabase
      .from("ai_chat_settings")
      .select("*")
      .eq("id", 1)
      .single();

    if (!settings) throw new Error("Settings not found");

    const apiKey = settings.api_key || process.env.OPENROUTER_API_KEY;
    if (!apiKey) return { success: false, error: "API Key not configured in environment" };

    try {
      const response = await callOpenRouter({
        apiKey,
        model: settings.model_id,
        temperature: settings.temperature,
        maxTokens: settings.max_tokens,
        systemPrompt: settings.system_instructions,
      }, data.message);

      return { success: true, response };
    } catch (err: any) {
      // Log exactly what's wrong for forensics
      console.error("[ai-chat] Test connection failed:", {
        message: err.message,
        stack: err.stack,
        apiKeyPrefix: apiKey ? `${apiKey.substring(0, 7)}...` : 'missing'
      });
      return { success: false, error: err.message || "Failed to connect to AI provider" };
    }
  });

export const getAIChatResponse = createServerFn({ method: "POST" })
  .inputValidator((data) => z.object({ 
    message: z.string(),
    isDemo: z.boolean().optional().default(false)
  }).parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: settings } = await supabaseAdmin
      .from("ai_chat_settings")
      .select("*")
      .eq("id", 1)
      .single();

    if (!settings || !settings.enabled) {
      return { response: "AI Chat is currently disabled." };
    }

    const apiKey = settings.api_key || process.env.OPENROUTER_API_KEY;
    if (!apiKey) return { response: settings.fallback_message };

    try {
      // FAQ Retrieval
      const { data: faqs, error: faqError } = await supabaseAdmin
        .rpc("get_active_faqs", { _is_demo: data.isDemo });
      
      let faqContext = "";
      if (faqs && faqs.length > 0) {
        // Simple keyword-based ranking or just providing top matches
        const query = data.message.toLowerCase();
        const relevantFaqs = faqs.filter(f => {
          const q = f.question.toLowerCase();
          const a = (f.answer_html || "").toLowerCase();
          const kw = (f.keywords || "").toLowerCase();
          return q.includes(query) || a.includes(query) || kw.split(",").some(k => query.includes(k.trim()));
        }).slice(0, 5); // Provide top 5 relevant FAQs

        if (relevantFaqs.length > 0) {
          faqContext = relevantFaqs.map(f => `Q: ${f.question}\nA: ${f.answer_html.replace(/<[^>]*>?/gm, '')}`).join("\n\n");
        }
      }

      const { callOpenRouter } = await import("./ai-chat.server");
      const response = await callOpenRouter({
        apiKey,
        model: settings.model_id,
        temperature: settings.temperature,
        maxTokens: settings.max_tokens,
        systemPrompt: settings.system_instructions,
      }, data.message, faqContext);

      return { response };
    } catch (err) {
      console.error("[ai-chat] Error:", err);
      return { response: settings.fallback_message };
    }
  });
